import { Alert, Button, Modal, Stack, type ModalProps } from "@mantine/core";
import { useForm } from "react-hook-form";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { useToast } from "../../../mantine/notifications/useToast";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type {
  ContractExtension,
  DirektvermarktungContractOverviewResponse
} from "../Direktvermarktung.types";
import { useDirektvermarktungContractExtensionMutation } from "../hooks/useDirektvermarktungContractExtensionMutation";
import { DIREKTVERMARKTUNG_CONTRACT_EXTENSION_FORM_DATA } from "./DirektVermarktungContractExtensionModal.constants";

interface DirektvermarktungContractExtensionModalProps extends Omit<
  ModalProps,
  "opened"
> {
  contract: DirektvermarktungContractOverviewResponse;
}

function DirektvermarktungContractExtensionModal({
  contract,
  ...modalProps
}: DirektvermarktungContractExtensionModalProps) {
  const { control, handleSubmit } = useForm<ContractExtension>();

  const extendContractMutation =
    useDirektvermarktungContractExtensionMutation();

  const { showErrorToast, showToast } = useToast();

  function onSubmit(data: ContractExtension) {
    extendContractMutation
      .mutateAsync({
        contractId: contract.id,
        newEndDate: data.last_date
      })
      .then(() => {
        modalProps.onClose();
        showToast({
          type: "success",
          message: "Vertrag erfolgreich verlängert."
        });
      })
      .catch((error) => showErrorToast(error));
  }

  if (!contract.last_date) {
    return null;
  }

  return (
    <Modal
      opened={contract !== null}
      size="lg"
      title="Vertrag verlängern"
      {...modalProps}
    >
      <Stack>
        <Alert {...AlertPresets.warning}>
          Bei der Vertragsverlängerung bleiben die bestehenden Konditionen
          unverändert, lediglich das Enddatum wird angepasst. Falls sich die
          Vertragskonditionen ändern, fügen Sie bitte einen neuen Vertrag hinzu.
        </Alert>

        <Stack gap="xs">
          Vertrag, der verlängert wird: <br />
          {contract.name} ({contract.first_date} - {contract.last_date})
        </Stack>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormFieldController
            control={control}
            data={{
              ...DIREKTVERMARKTUNG_CONTRACT_EXTENSION_FORM_DATA.last_date,
              initialCalendarDate: backendDateOrDateTimeToLuxonDateTime(
                contract.last_date
              ).plus({ days: 1 }),
              minDate: backendDateOrDateTimeToLuxonDateTime(
                contract.last_date
              ).plus({ days: 1 })
            }}
          />
          <ModalFooter justify="space-between">
            <Button variant="default" onClick={modalProps.onClose}>
              Abbrechen
            </Button>
            <Button loading={extendContractMutation.isPending} type="submit">
              Vertrag verlängern
            </Button>
          </ModalFooter>
        </form>
      </Stack>
    </Modal>
  );
}

export {
  DirektvermarktungContractExtensionModal,
  DirektvermarktungContractExtensionModalProps
};
